import React from 'react';
import { X } from 'lucide-react';
import { format } from 'date-fns';
import type { ColumnFiltersState, Table } from '@tanstack/react-table';
import lod from 'lodash';
import { formatISOToDisplayDate } from '@/lib/utils';
import { Badge } from '@/global/base/badge.tsx';
import { Input } from '@/global/base/input.tsx';
import { Button } from '@/global/base/button.tsx';
import type { ColumnMeta } from './DataTable';
import type { Option } from '@/global/form/FilteredSelectField';

type FiltersProps<TData> = {
    table: Table<TData>;
    globalFilter: string;
    setGlobalFilter: (value: string) => void;
    setColumnFilters: (filters: ColumnFiltersState) => void;
    setShowColumnModal: (show: boolean) => void;
    tableSlug?: string;
};

// TODO: This component can definitely use some reorganisation
// The JSX contains way too much logic making the code harder to read

// TODO: Fix the Type issues here

type DateRangeValue = {
    from?: string;
    to?: string;
};

const Filters = <TData,>({
    table,
    globalFilter,
    setGlobalFilter,
    setColumnFilters,
    setShowColumnModal,
    tableSlug = 'data-table'
}: FiltersProps<TData>) => {
    const { columnFilters } = table.getState();

    const hasFilters = columnFilters.length > 0;

    const filteredColumns = columnFilters.map((appliedFilter) => {
        const column = table.getColumn(appliedFilter.id);

        return {
            id: appliedFilter.id,
            colDef: column?.columnDef,
            getFilterValue: column?.getFilterValue,
            setFilterValue: column?.setFilterValue,
            value: appliedFilter.value
        };
    });

    const visibleColumnsLength = table.getVisibleLeafColumns().length;
    const allColumnsLength = table.getAllLeafColumns().length;

    const editColumnsText = `Edit Columns${allColumnsLength === visibleColumnsLength ? '' : ` (Showing ${visibleColumnsLength}/${allColumnsLength})`}`;

    const formatFilterValue = (filterValue: unknown, columnMeta: ColumnMeta | undefined) => {
        if (columnMeta?.type === 'date') {
            try {
                return formatISOToDisplayDate(filterValue as string);
            } catch {
                return String(filterValue);
            }
        } else if (columnMeta?.type === 'dateRange') {
            const dateRange = filterValue as DateRangeValue;
            const fromDate = dateRange?.from ? format(new Date(dateRange.from), 'dd/MM/yyyy') : '';
            const toDate = dateRange?.to ? format(new Date(dateRange.to), 'dd/MM/yyyy') : '';

            if (fromDate && toDate) {
                return `${fromDate} - ${toDate}`;
            } else if (fromDate) {
                return `From: ${fromDate}`;
            } else if (toDate) {
                return `To: ${toDate}`;
            } else {
                return 'Date range';
            }
        } else if (columnMeta?.type === 'select' && Array.isArray(columnMeta?.options) && columnMeta.options.length > 0) {
            const opts = columnMeta.options;
            const first = opts[0];
            if (typeof first === 'object' && first !== null && 'value' in first && 'label' in first) {
                const option = opts.find((o) => String((o as Option).value) === String(filterValue));
                if (option) {
                    const label = (option as Option).label;
                    return label.replace(/\s*\(\d+\)\s*$/, '');
                }
                return String(filterValue);
            }
        }

        return String(filterValue);
    };

    const hasFilterValue = (filterValue: unknown, columnMeta: ColumnMeta | undefined) => {
        if (columnMeta?.type === 'dateRange') {
            const dateRange = filterValue as DateRangeValue;

            return dateRange && (dateRange.from || dateRange.to);
        }

        return !lod.isEmpty(filterValue);
    };

    return (
        <div className="mt-4 py-2" data-testid={`${tableSlug}-filters`}>
            <div className="flex justify-between w-full">
                <div
                    className="flex space-x-7 text-left flex-wrap"
                    data-testid={`${tableSlug}-active-filters`}
                >
                    {filteredColumns.map((column) => {
                        if (column?.colDef === undefined) return null;

                        const filterValue = column.getFilterValue?.();
                        const columnMeta = column.colDef.meta as ColumnMeta;
                        const columnId = column.colDef.id ?? column.id;

                        if (hasFilterValue(filterValue, columnMeta)) {
                            return (
                                <div
                                    key={`column-${columnId}`}
                                    data-testid={`${tableSlug}-active-filter-${columnId}`}
                                >
                                    <div className="py-1 text-muted-foreground text-sm">
                                        {column.colDef.header as string}
                                    </div>
                                    <div className="flex space-x-1.5">
                                        <Badge variant="turqoise" className="font-normal py-2 px-3">
                                            {formatFilterValue(filterValue, columnMeta)}
                                            <button
                                                type="button"
                                                data-testid={`${tableSlug}-clear-filter-${columnId}`}
                                                onClick={() => column.setFilterValue?.(columnMeta?.type === 'dateRange' ? undefined : '')}
                                                className="ml-2 hover:text-muted-foreground text-primary bg-white rounded-full p-0.5"
                                            >
                                                <X className="h-3 w-3 bg-white rounded-full" />
                                            </button>
                                        </Badge>
                                    </div>
                                </div>
                            );
                        }
                        return null;
                    })}
                </div>
                <div className="text-right space-x-3 flex h-full">
                    <Button
                        variant="complement"
                        data-testid={`${tableSlug}-edit-columns-button`}
                        onClick={() => setShowColumnModal(true)}
                    >
                        {editColumnsText}
                    </Button>
                    <Input
                        data-testid={`${tableSlug}-global-filter-input`}
                        className="h-full"
                        autoComplete="off"
                        value={globalFilter ?? ''}
                        onChange={(event) => setGlobalFilter(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') setGlobalFilter(globalFilter);
                        }}
                        placeholder="Search..."
                    />
                </div>
            </div>
            {hasFilters &&
                <Button
                    variant="link"
                    className="font-bold text-xs underline px-1"
                    data-testid={`${tableSlug}-clear-filters-button`}
                    onClick={() => {
                        setColumnFilters([]);
                        setGlobalFilter('');
                    }}
                >
                    Clear Filters
                </Button>
            }
        </div>
    );
};

export default Filters;
